/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { TextViewProps } from '@airasia-phoenix/react-native/components/TextView';

import { EmailFieldProps } from '@airasia-phoenix/react-native/components/EmailFieldView';

import {
  ButtonViewProps,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { PasswordFieldViewProps } from '@airasia-phoenix/react-native/components/PasswordFieldView';
import { DisclaimerProps } from '../../utils/signupLoginUtils';

import { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import { pd } from '@airasia-phoenix/react-native/utils';
import {
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import { PasswordlessResponse } from '../../managers';
import Close from '@move-travel/react-native-phoenix-icons/Close';
import type {
  LoginError,
  onAppleLoginRequest,
  onFacebookLoginRequest,
  onGoogleLoginRequest,
  SocialLoginResult,
} from '../LoginView/types';

export type EmailSignUpMode = 'passwordless' | 'password';

export type SignUpPageProps = {
  onCloseIconClick?: () => void;
  headings?: {
    closeIcon: IconViewProps;
    greeting: TextViewProps;
    prompt: TextViewProps;
    action: TextViewProps;
    dividerText: TextViewProps;
  };
  emailFieldProps?: EmailFieldProps;
  passwordFieldProps?: PasswordFieldViewProps;
  primaryButtonProps?: ButtonViewProps;
  socialSignUps?: {
    facebook: { icon: IconViewProps; label: TextViewProps };
    google: { icon: IconViewProps; label: TextViewProps };
    apple: { icon: IconViewProps; label: TextViewProps };
    wechat: { icon: IconViewProps; label: TextViewProps };
  };
  disclaimer?: DisclaimerProps;
  onProceedToPersonalDetails?: (data: {
    email: string;
    password: string;
  }) => void;
  onSignUpError?: (error: Error) => void;
  onLoadingChange?: (loading: boolean) => void;
  emailSignUpMode?: EmailSignUpMode;
  prefilledEmail?: string;

  onPasswordLessEmailSignUpSuccess?: (data: PasswordlessResponse) => void;
  onEmailDomainDropdownChange?: (isOpen: boolean, dismiss: () => void) => void;
  onGoogleLoginRequest?: onGoogleLoginRequest;
  onFacebookLoginRequest?: onFacebookLoginRequest;
  onAppleLoginRequest?: onAppleLoginRequest;
  onSocialSignUpSuccess?: (result: SocialLoginResult) => void | Promise<void>;
  onSocialSignUpError?: (errorData: LoginError) => void;
};

export const defaultProps: SignUpPageProps = {
  headings: {
    closeIcon: {
      height: 24,
      width: 24,
      tint: getColor(pd.sys.colour.content_primary),
      testID: 'close-icon',
      children: <Close />,
    },
    greeting: {
      text: 'Sign Up',
      style: getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ),
    },
    prompt: {
      text: 'Already have an account? ',
      style: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.content_secondary,
      ),
    },
    action: {
      text: 'Log in instead',
      style: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.interactive_link,
      ),
    },
    dividerText: {
      text: 'Or',
      style: [
        getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
      ],
    },
  },
  emailFieldProps: {
    label: 'Email',
    disabled: false,
    value: '',
    isError: false,
    errorMessage: 'Please enter a valid email',
    suggestions: [],
    onError: () => {},
  },
  passwordFieldProps: {
    label: 'Password',
    disabled: false,
    value: '',
    isError: false,
    maxLength: 15,
    errorMessage: 'Not a valid password',
    onChangeText: () => {},
    onError: () => {},
  },
  primaryButtonProps: {
    text: 'Sign Up',
    type: ButtonViewType.PRIMARY_REGULAR,
    disabled: false,
    testID: 'submit-button',
  },
  socialSignUps: {
    facebook: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/facebook.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => console.log('Facebook pressed'),
        testID: 'facebook-icon',
      },
      label: {
        text: 'Facebook',
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'facebook-text',
      },
    },
    google: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/google.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => console.log('Google pressed'),
        testID: 'google-icon',
      },
      label: {
        text: 'Google',
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'google-text',
      },
    },
    apple: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/apple.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => console.log('Apple pressed'),
        testID: 'apple-icon',
      },
      label: {
        text: 'Apple',
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'apple-text',
      },
    },
    wechat: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/wechat.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => console.log('WeChat pressed'),
        testID: 'wechat-icon',
      },
      label: {
        text: 'WeChat',
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'wechat-text',
      },
    },
  },
  disclaimer: {
    text: "This platform is protected by reCAPTCHA. By logging in, you are deemed to have read and agreed to (i) AirAsia MOVE's Terms and conditions, Terms of Use and Privacy Statement; and (ii) Google's Terms of Service and Privacy Policy.",
    style: getTextStyle(
      pd.sys.typescale.caption_text,
      pd.sys.colour.content_secondary,
    ),
    spannableTextsProps: [
      {
        spannable: 'Terms and conditions',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
        onPress: () => console.log('Terms and conditions clicked'),
      },
      {
        spannable: 'Terms of Use',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
        onPress: () => console.log('Terms of Use clicked'),
      },
      {
        spannable: 'Privacy Statement',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
        onPress: () => console.log('Privacy Statement clicked'),
      },
      {
        spannable: 'Terms of Service',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
        onPress: () => console.log('Terms of Service clicked'),
      },
      {
        spannable: 'Privacy Policy',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
        onPress: () => console.log('Privacy Policy clicked'),
      },
    ],
  },

  emailSignUpMode: 'passwordless',
  onPasswordLessEmailSignUpSuccess: () => {},
};
